// Global Instructions Rule Applied!
//
// LPs grid data (FUND MANAGEMENT → LPs): the canonical grid pattern (LP
// Allocation is the sibling) — the Redux slice holds rows + active filters /
// sorting + paging, user preferences hydrate and persist filters, sorting and
// column order through /global-configs/user-preferences, filter options load
// once, and load-more appends the next page for TableView's infinite scroll.
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { AppDispatch, RootState } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import {
  setLps,
  appendLps,
  setLpsActiveFilters,
  setLpsActiveSorting,
  updateLpsRow
} from '../../../state_manager/state_update_actions/lps/index.ts'
import { LpRow } from '../../../state_manager/data_models/lps_model.ts'
import { fetchLps, fetchLpsFilterOptions } from '../services/lpsService.ts'
import { useMenuItemUserPreference } from '../../global_configs/useMenuItemUserPreference.ts'
import {
  mergeUserPreferenceRowData,
  removeUserPreferenceForMenuItem
} from '../../../state_manager/state_update_actions/global_configs/actions.ts'
import {
  areFilterValuesEqual,
  areSortConfigsEqual,
  FilterValue,
  SortConfig,
  PREF_TABLE_COLUMN_KEYS,
  PREF_TABLE_FILTERS,
  PREF_TABLE_SORTING,
  resolveTableFiltersFromPreferenceRow,
  resolveTableSortingFromPreferenceRow
} from '../../global_configs/tableViewPreferences.ts'
import { useReportingDefaultSorting } from '../../global_configs/useReportingDefaultSorting.ts'
import { MENU_ITEM_KEY_LPS } from '../../../state_manager/globals.ts'
import { fetchGlobalConfigsThunk } from '../../global_configs/controller.ts'
import { useLpsReportId } from '../../../state_manager/hooks/useLpsReportId.ts'

const DEFAULT_LPS_FILTERS: FilterValue[] = []
const PAGE_SIZE = 100

export const useLps = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { menuItemId, preferenceRow, persistPreference, deletePreference } = useMenuItemUserPreference(MENU_ITEM_KEY_LPS)
  const reportId = useLpsReportId()
  const defaultTableSorting = useReportingDefaultSorting(reportId)

  const [dataLoading, setDataLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [prefsReady, setPrefsReady] = useState(false)
  const [filterOptions, setFilterOptions] = useState<Record<string, Array<{ value: string; label: string }>>>({})
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(false)
  const skipPersistRef = useRef(true)

  const data = useSelector((state: RootState) => state.lps.data)
  const totalCount = useSelector((state: RootState) => state.lps.totalCount)
  const activeFilters = useSelector((state: RootState) => state.lps.activeFilters)
  const activeSorting = useSelector((state: RootState) => state.lps.activeSorting)
  const hasMore = useSelector((state: RootState) => state.lps.hasMore)
  const currentPage = useSelector((state: RootState) => state.lps.currentPage)
  const menuItems = useSelector((state: RootState) => state.globalConfigs?.menu_items ?? [])

  useEffect(() => {
    if (menuItems.length === 0) return
    if (!menuItemId) {
      dispatch(setLpsActiveFilters({ filters: DEFAULT_LPS_FILTERS }))
      dispatch(setLpsActiveSorting({ sorting: defaultTableSorting }))
      setPrefsReady(true)
      return
    }
    const filters = resolveTableFiltersFromPreferenceRow(preferenceRow, DEFAULT_LPS_FILTERS)
    const sorting = resolveTableSortingFromPreferenceRow(preferenceRow, defaultTableSorting)
    if (!areFilterValuesEqual(filters, activeFilters)) dispatch(setLpsActiveFilters({ filters }))
    if (!areSortConfigsEqual(sorting, activeSorting)) dispatch(setLpsActiveSorting({ sorting }))
    setPrefsReady(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuItemId, menuItems.length, preferenceRow, defaultTableSorting, dispatch])

  const handleFetch = useCallback(
    async (sorting: SortConfig[] = [], filters?: FilterValue[]) => {
      try {
        setDataLoading(true)
        const token = getAccessToken(store.getState().auth)
        const res = await fetchLps(
          { sorting: sorting.map((s) => ({ key: s.key, direction: s.direction })), filters: filters ?? [], page: 1, page_size: PAGE_SIZE },
          token
        )
        dispatch(setLps({ response: { data: res.data, hasMore: res.has_more, totalCount: res.count } }))
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.error('Error fetching LPs:', e)
      } finally {
        setDataLoading(false)
      }
    },
    [dispatch]
  )

  const handleSetFilters = useCallback(
    (filters: FilterValue[]) => {
      if (areFilterValuesEqual(filters, activeFilters)) return
      dispatch(setLpsActiveFilters({ filters }))
    },
    [dispatch, activeFilters]
  )

  const handleSetSorting = useCallback(
    (sorting: SortConfig[]) => {
      if (areSortConfigsEqual(sorting, activeSorting)) return
      dispatch(setLpsActiveSorting({ sorting }))
    },
    [dispatch, activeSorting]
  )

  const handleResetFilters = useCallback(() => {
    if (!menuItemId) {
      dispatch(setLpsActiveFilters({ filters: DEFAULT_LPS_FILTERS }))
      return
    }
    void (async () => {
      const result = await deletePreference()
      if (!result.success) return
      dispatch(removeUserPreferenceForMenuItem(menuItemId))
      skipPersistRef.current = true
      dispatch(setLpsActiveFilters({ filters: DEFAULT_LPS_FILTERS }))
      dispatch(setLpsActiveSorting({ sorting: defaultTableSorting }))
    })()
  }, [dispatch, menuItemId, deletePreference, defaultTableSorting])

  const persistMergedPreference = useCallback(
    async (extra: Record<string, unknown> = {}) => {
      if (!menuItemId) return { success: false as const, error: 'No menu item' }
      const reports = store.getState().globalConfigs?.user_preferences_reports ?? []
      const hadExistingRow = reports.some((p: any) => p.menu_item_id === menuItemId)
      const row = reports.find((p: any) => p.menu_item_id === menuItemId)
      const merged: Record<string, unknown> = {
        ...(row?.data ?? {}),
        ...extra,
        [PREF_TABLE_FILTERS]: activeFilters,
        [PREF_TABLE_SORTING]: activeSorting
      }
      const result = await persistPreference(undefined, merged)
      if (result.success) {
        if (hadExistingRow) dispatch(mergeUserPreferenceRowData(menuItemId, merged))
        else {
          const user = store.getState().auth.user
          if (user) await fetchGlobalConfigsThunk(user)(dispatch)
        }
      }
      return result
    },
    [menuItemId, activeFilters, activeSorting, persistPreference, dispatch]
  )

  const persistColumnFieldOrder = useCallback(
    async (fieldNames: string[]) => persistMergedPreference({ [PREF_TABLE_COLUMN_KEYS]: fieldNames }),
    [persistMergedPreference]
  )

  const resetColumnLayoutToDefault = useCallback(async () => {
    if (!menuItemId) return { success: false as const, error: 'No menu item' }
    const reports = store.getState().globalConfigs?.user_preferences_reports ?? []
    const hadExistingRow = reports.some((p: any) => p.menu_item_id === menuItemId)
    const row = reports.find((p: any) => p.menu_item_id === menuItemId)
    const base: Record<string, unknown> = { ...(row?.data ?? {}) }
    delete base[PREF_TABLE_COLUMN_KEYS]
    const merged: Record<string, unknown> = { ...base, [PREF_TABLE_FILTERS]: activeFilters, [PREF_TABLE_SORTING]: activeSorting }
    const result = await persistPreference(undefined, merged)
    if (result.success) {
      if (hadExistingRow) dispatch(mergeUserPreferenceRowData(menuItemId, merged))
      else {
        const user = store.getState().auth.user
        if (user) await fetchGlobalConfigsThunk(user)(dispatch)
      }
    }
    return result
  }, [menuItemId, activeFilters, activeSorting, persistPreference, dispatch])

  const handleLoadMore = useCallback(
    async (page: number, pageSize: number) => {
      if (loadingMore || !hasMore) return
      try {
        setLoadingMore(true)
        const token = getAccessToken(store.getState().auth)
        const res = await fetchLps(
          {
            sorting: activeSorting.map((s) => ({ key: s.key, direction: s.direction })),
            filters: activeFilters,
            page,
            page_size: pageSize || PAGE_SIZE
          },
          token
        )
        dispatch(appendLps({ response: { data: res.data, hasMore: res.has_more, totalCount: res.count } }))
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.error('Error loading more LPs:', e)
      } finally {
        setLoadingMore(false)
      }
    },
    [dispatch, activeFilters, activeSorting, hasMore, loadingMore]
  )

  useEffect(() => {
    if (!prefsReady) return
    const token = getAccessToken(store.getState().auth)
    if (!token) return
    let cancelled = false
    setFilterOptionsLoading(true)
    ;(async () => {
      try {
        const res = await fetchLpsFilterOptions(token)
        if (!cancelled) setFilterOptions(res as unknown as Record<string, Array<{ value: string; label: string }>>)
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.error('LPs filter options failed:', e)
      } finally {
        if (!cancelled) setFilterOptionsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [prefsReady])

  useEffect(() => {
    if (!prefsReady) return
    const token = getAccessToken(store.getState().auth)
    if (token) handleFetch(activeSorting, activeFilters)
  }, [prefsReady, activeFilters, activeSorting, handleFetch])

  useEffect(() => {
    if (!prefsReady || !menuItemId) return
    if (skipPersistRef.current) {
      skipPersistRef.current = false
      return
    }
    void persistMergedPreference().catch((err) => {
      if (process.env.NODE_ENV === 'development') console.error('Failed to persist LPs preferences:', err)
    })
  }, [prefsReady, menuItemId, activeFilters, activeSorting, persistMergedPreference])

  const patchRow = useCallback(
    (accountId: string, updates: Partial<LpRow>) => dispatch(updateLpsRow({ accountId, updates })),
    [dispatch]
  )

  return {
    data,
    totalCount,
    dataLoading,
    activeFilters,
    activeSorting,
    preferenceRow,
    hasMore,
    currentPage,
    loadingMore,
    filterOptions,
    filterOptionsLoading,
    handleFetch,
    handleSetFilters,
    handleSetSorting,
    handleResetFilters,
    handleLoadMore,
    patchRow,
    defaultTableFilters: DEFAULT_LPS_FILTERS,
    defaultTableSorting,
    persistColumnFieldOrder,
    resetColumnLayoutToDefault
  }
}
